import axios from 'axios';

import {PaymentSourceRole} from 'enums';
import {
    AccountPlanId, BillingAddress, BillingPlatformCustomer, BillingPlatformInvoice, BillingPlatformPaymentSource,
    BillingPlatformSubscription, BillingPlatformTransaction
} from 'types';

import {API_URL} from './constants';

const getBillingPlatformData = async ({short} = {short: false}): Promise<{
    customer: BillingPlatformCustomer,
    subscription: BillingPlatformSubscription,
    subscriptionNextPeriod?: BillingPlatformSubscription,
    paymentSources: BillingPlatformPaymentSource[],
    invoices: BillingPlatformInvoice[],
    transactions: BillingPlatformTransaction[]
}> => {
    const path = short ? 'data-short' : 'data';
    const response = await axios.get(`${API_URL}/billing-platform/${path}`);
    return response.data;
};

const getBillingPlatformCustomer = async (): Promise<{
    customer: BillingPlatformCustomer,
    subscription: BillingPlatformSubscription
}> => {
    const response = await axios.get(`${API_URL}/billing-platform/subscription`);
    return response.data;
};

const payUnbilledCharges = async (data: {paymentToken: string, maxUsers: number}): Promise<void> => {
    const response = await axios.post(`${API_URL}/billing-platform/unbilled-charges`, data);
    return response.data;
};

const reactivateSubscription = async (data: {
    paymentToken?: string,
    maxUsers?: number,
    selectedPaymentSourceId?: string,
    usersToRemove?: string[]
}): Promise<void> => {
    const response = await axios.post(`${API_URL}/billing-platform/subscription/reactivate`, data);
    return response.data;
};

const getBrowserCredentials = async (): Promise<{site: string, publishableKey: string}> => {
    const response = await axios.get(`${API_URL}/billing-platform/browser-credentials`);
    return response.data;
};

const getPartnerStackCredentials = async (): Promise<{publicKey: string}> => {
    const response = await axios.get(`${API_URL}/billing-platform/partner-stack-credentials`);
    return response.data;
};

const pauseSubscription = async (): Promise<void> => {
    const response = await axios.post(`${API_URL}/billing-platform/subscription/scheduled-pause`);
    return response.data;
};

const removePauseSubscription = async (): Promise<void> => {
    const response = await axios.delete(`${API_URL}/billing-platform/subscription/scheduled-pause`);
    return response.data;
};

const editSubscription = async (id: string, data: {quantity?: number, endOfTerm: boolean, planId?: AccountPlanId}): Promise<void> => {
    const response = await axios.put(`${API_URL}/billing-platform/subscription/${id}`, data);
    return response.data;
};

const updateBillingAddress = async (address: BillingAddress): Promise<BillingPlatformCustomer> => {
    const response = await axios.post(`${API_URL}/billing-platform/billing-address`, address);
    return response.data;
};

const removePaymentSource = async (id: string): Promise<void> => {
    const response = await axios.delete(`${API_URL}/billing-platform/payment-source/${id}`);
    return response.data;
};

const addPaymentSource = async (data: {paymentToken: string, maxUsers?: number}): Promise<void> => {
    const response = await axios.post(`${API_URL}/billing-platform/payment-source`, data);
    return response.data;
};

const updatePaymentSourceRole = async (paymentSourceId: string, role: PaymentSourceRole): Promise<void> => {
    const response = await axios.post(`${API_URL}/billing-platform/payment-source/role/${paymentSourceId}`, {role});
    return response.data;
};

export const billingPlatformApi = {
    getBillingPlatformData,
    getBillingPlatformCustomer,
    getBrowserCredentials,
    getPartnerStackCredentials,
    payUnbilledCharges,
    pauseSubscription,
    removePauseSubscription,
    editSubscription,
    updateBillingAddress,
    removePaymentSource,
    addPaymentSource,
    updatePaymentSourceRole,
    reactivateSubscription
};